export const DEFAULT_AUTH_INTENT = "application";
const STANDARD_AUTH_THEME = "client";

// Shared-auth contract: application/client stay phone-first; broker/lender/staff/investor/referrer stay email-first.
// Shared-auth visuals stay on the standardized client/application palette for every login entry.
export const AUTH_INTENT_DETAILS = Object.freeze({
    application: {
        label: "Application form",
        shortLabel: "Application",
        description: "Continue your application form.",
        availability: "live",
        primaryAuthChannel: "sms",
        theme: STANDARD_AUTH_THEME,
    },
    broker: {
        label: "Broker Portal",
        shortLabel: "Broker Portal",
        description: "Access the Broker Portal workspace.",
        availability: "live",
        primaryAuthChannel: "email",
        theme: STANDARD_AUTH_THEME,
    },
    lender: {
        label: "xPortal",
        shortLabel: "Lender",
        description: "Access the xPortal lender workspace.",
        availability: "live",
        primaryAuthChannel: "email",
        theme: STANDARD_AUTH_THEME,
    },
    staff: {
        label: "sPortal",
        shortLabel: "Staff",
        description: "Access internal staff tools.",
        availability: "live",
        primaryAuthChannel: "email",
        theme: STANDARD_AUTH_THEME,
    },
    investor: {
        label: "iPortal",
        shortLabel: "iPortal",
        description: "Access iPortal.",
        availability: "live",
        primaryAuthChannel: "email",
        theme: STANDARD_AUTH_THEME,
    },
    client: {
        label: "cPortal",
        shortLabel: "Client",
        description: "Access cPortal.",
        availability: "live",
        primaryAuthChannel: "sms",
        theme: STANDARD_AUTH_THEME,
    },
    referrer: {
        label: "Referrer portal",
        shortLabel: "Referrer",
        description: "Access the referrer workspace.",
        availability: "live",
        primaryAuthChannel: "email",
        theme: STANDARD_AUTH_THEME,
    },
});

export const AUTH_INTENTS = Object.freeze(Object.keys(AUTH_INTENT_DETAILS));
const VALID_INTENTS = new Set(AUTH_INTENTS);

// Canonical mapping of intent aliases to handle common naming variations and client configurations.
// This prevents the login portal from breaking (e.g. falling back to the default application flow)
// when apps or links pass slightly different names.
const INTENT_ALIASES = Object.freeze({
    // Lender / xPortal aliases
    xportal: "lender",
    lenderportal: "lender",
    "lender-portal": "lender",
    lender: "lender",

    // Broker Portal aliases
    brokerportal: "broker",
    "broker-portal": "broker",
    broker: "broker",

    // Staff / sPortal aliases
    sportal: "staff",
    staffportal: "staff",
    "staff-portal": "staff",
    staff: "staff",

    // Investor / iPortal aliases
    iportal: "investor",
    investorportal: "investor",
    "investor-portal": "investor",
    investor: "investor",

    // Client / cPortal aliases
    cportal: "client",
    clientportal: "client",
    "client-portal": "client",
    client: "client",

    // Referrer aliases
    referrerportal: "referrer",
    "referrer-portal": "referrer",
    referrer: "referrer",

    // Application form aliases
    applicationform: "application",
    "application-form": "application",
    application: "application",
});

const PENDING_LAUNCH_CONTEXT_KEY = "pendingLaunchContext";
const REMEMBER_DEVICE_PREFERENCE_KEY = "authRememberDevicePreference";
const REMEMBERED_SIGN_IN_EMAIL_KEY = "authRememberedSignInEmail";
const APPLICATION_RETURN_PATHS = new Set([
    "/start",
    "/application",
    "/application/start",
]);
const XPORTAL_APPLICATION_RETURN_SOURCES = new Set([
    "xportal",
    "lender-portal",
]);
const XPORTAL_LAYER_INTENTS = new Set(["broker", "lender"]);
const XPORTAL_LAYER_ONLY_RETURN_PREFIXES = Object.freeze({
    broker: [
        "/deal-checker",
        "/leads",
        "/lenders",
        "/referrer",
        "/referrers",
        "/referral-partner-leads",
        "/referred-deals",
    ],
    lender: [
        "/appetite-profiles",
        "/approved-products",
        "/batches",
        "/brokers",
        "/diagnostics",
        "/forms",
        "/indicative-term-requests",
        "/loans",
        "/master-product-sets",
        "/policy-links",
        "/postcode-policy-sets",
        "/product-guides",
        "/regression-packs",
        "/regression-runs",
        "/workbench",
    ],
});
const RAW_XPORTAL_INTENTS = new Set(["xportal", "x-portal", "x_portal"]);
const XPORTAL_WORKSPACE_DETAILS = Object.freeze({
    label: "xPortal",
    shortLabel: "xPortal",
    description: "Access the xPortal workspace.",
});
const CASE_SENSITIVE_PORTAL_LABELS = new Set(["iPortal", "xPortal", "cPortal", "sPortal"]);

function sanitiseString(value) {
    if (value === null || value === undefined) {
        return null;
    }

    const trimmed = String(value).trim();
    return trimmed || null;
}

function sanitiseEmail(value) {
    const email = sanitiseString(value)?.toLowerCase() || null;
    return email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : null;
}

export function sanitiseSource(value) {
    return sanitiseString(value)?.toLowerCase() || null;
}

export function sanitiseReturnTo(value) {
    const trimmed = sanitiseString(value);

    if (
        !trimmed ||
        trimmed.startsWith("//") ||
        /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ||
        /[\r\n]/.test(trimmed)
    ) {
        return null;
    }

    if (!trimmed.startsWith("/") && !trimmed.startsWith("?") && !trimmed.startsWith("#")) {
        return null;
    }

    return trimmed;
}

export function parseIntentOrNull(value) {
    const normalised = sanitiseString(value)?.toLowerCase().trim() || null;
    if (!normalised) {
        return null;
    }

    // Direct check in the alias mapping first (e.g. "xportal" -> "lender")
    if (INTENT_ALIASES[normalised]) {
        return INTENT_ALIASES[normalised];
    }

    // Strip hyphens and underscores for a looser, more resilient match (e.g. "lender_portal" -> "lender")
    const clean = normalised.replace(/[-_]/g, "");
    if (INTENT_ALIASES[clean]) {
        return INTENT_ALIASES[clean];
    }

    return VALID_INTENTS.has(normalised) ? normalised : null;
}

export function parseIntent(value, fallback = DEFAULT_AUTH_INTENT) {
    return parseIntentOrNull(value) || fallback;
}

function isRawXPortalIntent(value) {
    const normalised = sanitiseString(value)?.toLowerCase().trim() || null;
    return RAW_XPORTAL_INTENTS.has(normalised);
}

function isApplicationReturnTo(value) {
    const returnTo = sanitiseReturnTo(value);

    if (!returnTo) {
        return false;
    }

    try {
        const parsed = new URL(returnTo, "http://localhost");
        return APPLICATION_RETURN_PATHS.has(parsed.pathname) || parsed.pathname.startsWith("/apply/");
    } catch (_error) {
        return false;
    }
}

function resolveLaunchIntent({ rawIntent, intent, source, returnTo }) {
    const parsedIntent = isRawXPortalIntent(rawIntent)
        ? "broker"
        : parseIntentOrNull(rawIntent) || intent;

    if (
        parsedIntent === "broker" &&
        XPORTAL_APPLICATION_RETURN_SOURCES.has(sanitiseSource(source)) &&
        isApplicationReturnTo(returnTo)
    ) {
        return "application";
    }

    return parsedIntent || DEFAULT_AUTH_INTENT;
}

export function isXPortalLayerLaunch(intent, source) {
    return XPORTAL_LAYER_INTENTS.has(parseIntent(intent)) &&
        XPORTAL_APPLICATION_RETURN_SOURCES.has(sanitiseSource(source));
}

function pickXPortalLayerIntent(value) {
    const intent = parseIntentOrNull(value);
    return XPORTAL_LAYER_INTENTS.has(intent) ? intent : null;
}

function pickSingleAllowedXPortalLayerIntent(resolution) {
    const xPortalPortals = Array.isArray(resolution?.allowedPortals)
        ? resolution.allowedPortals
            .map((portal) => pickXPortalLayerIntent(portal?.portalId || portal?.intent))
            .filter(Boolean)
        : [];
    const uniqueIntents = [...new Set(xPortalPortals)];

    return uniqueIntents.length === 1 ? uniqueIntents[0] : null;
}

function normalisePathForPrefixCheck(value) {
    const returnTo = sanitiseReturnTo(value);
    if (!returnTo) {
        return null;
    }

    try {
        const parsed = new URL(returnTo, "http://localhost");
        const pathname = parsed.pathname.replace(/\/+$/, "");
        return pathname || "/";
    } catch (_error) {
        return null;
    }
}

function isPathWithinPrefixes(pathname, prefixes = []) {
    const normalizedPathname = normalisePathForPrefixCheck(pathname);
    if (!normalizedPathname) {
        return false;
    }

    return prefixes.some((prefix) => {
        const normalizedPrefix = normalisePathForPrefixCheck(prefix);
        return normalizedPrefix &&
            (normalizedPathname === normalizedPrefix || normalizedPathname.startsWith(`${normalizedPrefix}/`));
    });
}

export function resolveXPortalLayerIntentFromAuthResponse(authResponse, fallbackIntent, source = null) {
    const fallback = parseIntent(fallbackIntent);

    if (!isXPortalLayerLaunch(fallback, source)) {
        return fallback;
    }

    const resolution = authResponse?.resolution || null;
    const resolvedIntent = [
        authResponse?.challenge?.metadata?.launchContext?.intent,
        authResponse?.challenge?.intent,
        authResponse?.session?.bootstrapContext?.resolvedIntent,
        resolution?.preferredPortal?.portalId,
        pickSingleAllowedXPortalLayerIntent(resolution),
    ]
        .map(pickXPortalLayerIntent)
        .find(Boolean);

    return resolvedIntent || fallback;
}

export function resolveXPortalLayerReturnTo(returnTo, requestedIntent, resolvedIntent, source = null) {
    const sanitisedReturnTo = sanitiseReturnTo(returnTo);
    const requested = parseIntent(requestedIntent);
    const resolved = parseIntent(resolvedIntent);

    if (!sanitisedReturnTo || !isXPortalLayerLaunch(resolved, source) || requested === resolved) {
        return sanitisedReturnTo;
    }

    const otherLayer = resolved === "broker" ? "lender" : "broker";
    if (isPathWithinPrefixes(sanitisedReturnTo, XPORTAL_LAYER_ONLY_RETURN_PREFIXES[otherLayer])) {
        return "/";
    }

    return sanitisedReturnTo;
}

export function getIntentDetails(intent, source = null) {
    const parsedIntent = parseIntent(intent);
    const details = AUTH_INTENT_DETAILS[parsedIntent];

    if (isXPortalLayerLaunch(parsedIntent, source)) {
        return {
            ...details,
            ...XPORTAL_WORKSPACE_DETAILS,
        };
    }

    return details;
}

export function formatIntentDestinationReference(label) {
    const value = sanitiseString(label) || "";
    return CASE_SENSITIVE_PORTAL_LABELS.has(value) ? value : value.toLowerCase();
}

export function getIntentTheme(intent) {
    return getIntentDetails(intent)?.theme || "client";
}

export function getPrimaryAuthChannel(intent) {
    return getIntentDetails(intent)?.primaryAuthChannel || "sms";
}

export function isEmailFirstIntent(intent) {
    return getPrimaryAuthChannel(intent) === "email";
}

function safeBtoa(str) {
    if (typeof window !== "undefined" && typeof window.btoa === "function") {
        return window.btoa(str);
    }
    if (typeof Buffer !== "undefined") {
        return Buffer.from(str, "utf-8").toString("base64");
    }
    return btoa(str);
}

function safeAtob(str) {
    if (typeof window !== "undefined" && typeof window.atob === "function") {
        return window.atob(str);
    }
    if (typeof Buffer !== "undefined") {
        return Buffer.from(str, "base64").toString("utf-8");
    }
    return atob(str);
}

export function buildLaunchSearch(params = {}) {
    const intent = parseIntentOrNull(params.intent);
    const source = sanitiseSource(params.source);
    const returnTo = sanitiseReturnTo(params.returnTo);
    const brokerageId = sanitiseString(params.brokerageId);

    const payload = {};
    if (intent) payload.intent = intent;
    if (source) payload.source = source;
    if (returnTo) payload.returnTo = returnTo;
    if (brokerageId) payload.brokerageId = brokerageId;

    if (Object.keys(payload).length === 0) {
        return "";
    }

    try {
        const jsonStr = JSON.stringify(payload);
        const base64 = safeBtoa(jsonStr);
        const urlSafeBase64 = base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
        
        // Hardening: Always append the individual query parameters as fallback query parameters.
        // This ensures the login app can parse the intent even if base64/JSON decoding of 'ctx' fails.
        const search = new URLSearchParams();
        search.set("ctx", urlSafeBase64);
        if (intent) search.set("intent", intent);
        if (source) search.set("source", source);
        if (returnTo) search.set("returnTo", returnTo);
        if (brokerageId) search.set("_xctx", brokerageId);
        
        return `?${search.toString()}`;
    } catch (_error) {
        // Fallback to plain query parameters if JSON.stringify/safeBtoa fails
        const search = new URLSearchParams();
        if (intent) search.set("intent", intent);
        if (source) search.set("source", source);
        if (returnTo) search.set("returnTo", returnTo);
        if (brokerageId) search.set("_xctx", brokerageId);
        const query = search.toString();
        return query ? `?${query}` : "";
    }
}

export function buildLaunchPath(pathname, params = {}) {
    return `${pathname}${buildLaunchSearch(params)}`;
}

export function getLaunchContext(search = window.location.search) {
    const params = new URLSearchParams(search);
    const ctxToken = params.get("ctx");
    let intent = null;
    let source = null;
    let brokerageId = null;
    let returnTo = null;
    let rawIntent = null;

    if (ctxToken) {
        try {
            let base64 = ctxToken.replace(/-/g, "+").replace(/_/g, "/");
            while (base64.length % 4) {
                base64 += "=";
            }
            const decoded = JSON.parse(safeAtob(base64));
            rawIntent = decoded.intent;
            intent = parseIntentOrNull(decoded.intent);
            source = sanitiseSource(decoded.source);
            brokerageId = sanitiseString(decoded.brokerageId);
            returnTo = sanitiseReturnTo(decoded.returnTo);
        } catch (_error) {
            // Ignore error, fallback to parsing query parameters normally
        }
    }

    if (!rawIntent) {
        rawIntent = sanitiseString(params.get("intent"));
    }
    const sourceFromQuery = sanitiseSource(params.get("source"));
    const sourceFromIntent = isRawXPortalIntent(rawIntent) ? "xportal" : null;
    const resolvedSource = sourceFromQuery || source || sourceFromIntent;
    const resolvedReturnTo = sanitiseReturnTo(params.get("returnTo")) || returnTo;
    const parsedIntent = resolveLaunchIntent({
        rawIntent,
        intent,
        source: resolvedSource,
        returnTo: resolvedReturnTo,
    });

    return {
        rawIntent: rawIntent || params.get("intent") || null,
        intent: parsedIntent,
        hasExplicitIntent: Boolean(rawIntent || params.get("intent")),
        isValidIntent: !(rawIntent || params.get("intent")) || Boolean(parseIntentOrNull(rawIntent)),
        source: resolvedSource,
        brokerageId: sanitiseString(params.get("_xctx") || params.get("brokerageId")) || brokerageId,
        returnTo: resolvedReturnTo,
    };
}

export function persistLaunchContext(context = {}) {
    if (typeof window === "undefined") {
        return;
    }

    const nextContext = {
        intent: parseIntent(context.intent),
        source: sanitiseSource(context.source),
        brokerageId: sanitiseString(context.brokerageId),
        returnTo: sanitiseReturnTo(context.returnTo),
        hasExplicitIntent: context.hasExplicitIntent === true,
        rememberDevice: context.rememberDevice === true,
    };

    window.sessionStorage.setItem(PENDING_LAUNCH_CONTEXT_KEY, JSON.stringify(nextContext));
}

export function readPersistedLaunchContext() {
    if (typeof window === "undefined") {
        return null;
    }

    try {
        const parsed = JSON.parse(window.sessionStorage.getItem(PENDING_LAUNCH_CONTEXT_KEY) || "null");
        if (!parsed) {
            return null;
        }

        return {
            intent: parseIntent(parsed.intent),
            source: sanitiseSource(parsed.source),
            brokerageId: sanitiseString(parsed.brokerageId),
            returnTo: sanitiseReturnTo(parsed.returnTo),
            hasExplicitIntent: parsed.hasExplicitIntent === true,
            rememberDevice: parsed.rememberDevice === true,
        };
    } catch (_error) {
        return null;
    }
}

export function getEffectiveLaunchContext(search = window.location.search) {
    const queryContext = getLaunchContext(search);

    if (queryContext.hasExplicitIntent) {
        return queryContext;
    }

    const persisted = readPersistedLaunchContext();
    if (!persisted) {
        return queryContext;
    }

    return {
        ...queryContext,
        intent: persisted.intent || queryContext.intent,
        source: persisted.source || queryContext.source,
        brokerageId: persisted.brokerageId || queryContext.brokerageId,
        returnTo: persisted.returnTo || queryContext.returnTo,
        hasExplicitIntent: persisted.hasExplicitIntent || queryContext.hasExplicitIntent,
        isValidIntent: true,
        rawIntent: persisted.intent || queryContext.rawIntent,
        rememberDevice: persisted.rememberDevice === true,
    };
}

export function readRememberDevicePreference() {
    if (typeof window === "undefined") {
        return null;
    }

    const value = window.localStorage.getItem(REMEMBER_DEVICE_PREFERENCE_KEY);

    if (value === "true") {
        return true;
    }

    if (value === "false") {
        return false;
    }

    return null;
}

export function persistRememberDevicePreference(value) {
    if (typeof window === "undefined") {
        return;
    }

    window.localStorage.setItem(REMEMBER_DEVICE_PREFERENCE_KEY, value === true ? "true" : "false");
}

export function readRememberedSignInEmail() {
    if (typeof window === "undefined") {
        return "";
    }

    return sanitiseEmail(window.localStorage.getItem(REMEMBERED_SIGN_IN_EMAIL_KEY)) || "";
}

export function persistRememberedSignInEmail(value) {
    if (typeof window === "undefined") {
        return "";
    }

    const email = sanitiseEmail(value);
    if (!email) {
        return "";
    }

    window.localStorage.setItem(REMEMBERED_SIGN_IN_EMAIL_KEY, email);
    return email;
}
